import React, { useEffect, useState } from "react";
import { Pencil, Trash2, Plus, X } from "lucide-react";
import { toast } from "react-toastify";
import {
  getDevelopers,
  addDeveloper,
  updateDeveloper,
  deleteDeveloper,
} from "../../../services/api";
import "./DevelopersManagement.css";

const empty = { name: "", description: "" };

export default function Developers() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Delete modal
  const [deleteId, setDeleteId] = useState(null);
  const [deleteName, setDeleteName] = useState("");
  const [deleting, setDeleting] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const list = await getDevelopers();
      setItems(list || []);
    } catch (err) {
      toast.error(err.message || "Failed to load developers");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  function validate() {
    const map = {};
    if (!form.name.trim()) map.name = "Developer name is required";
    setErrors(map);
    return Object.keys(map).length === 0;
  }

  async function submit(e) {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      if (editing) {
        const res = await updateDeveloper(editing, form);
        toast.success(res.message || "Developer updated");
      } else {
        const res = await addDeveloper(form);
        toast.success(res.message || "Developer added");
      }
      setForm(empty);
      setEditing(null);
      setErrors({});
      await refresh();
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  function openDeleteModal(d) {
    setDeleteId(d.id);
    setDeleteName(d.name || d.developerName || "this developer");
  }

  function closeDeleteModal() {
    if (deleting) return;
    setDeleteId(null);
    setDeleteName("");
  }

  async function confirmDelete() {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await deleteDeveloper(deleteId);
      toast.success("Developer deleted");
      setDeleteId(null);
      setDeleteName("");
      await refresh();
    } catch (err) {
      toast.error(err.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  }

  function onEdit(d) {
    setEditing(d.id);
    setForm({
      name: d.name || d.developerName || "",
      description: d.description || "",
    });
    setErrors({});
  }

  function onCancel() {
    setEditing(null);
    setForm(empty);
    setErrors({});
  }

  return (
    <div className="dev">
      <div className="dev-head">
        <div>
          <div className="dev-eyebrow">Master data</div>
          <h1 className="dev-title">Developers</h1>
          <p className="dev-sub">
            Builders and developers used across property listings —{" "}
            {items.length} total.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="dev-card">
        <div className="dev-card-head">
          {editing ? "Edit developer" : "Add new developer"}
        </div>

        <div className="dev-fields">
          <div>
            <label className="dev-label">Developer name</label>
            <input
              className="dev-input"
              placeholder="Godrej Properties"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              disabled={saving}
            />
            {errors.name && <p className="dev-err">{errors.name}</p>}
          </div>

          <div>
            <label className="dev-label">Description</label>
            <textarea
              rows={4}
              className="dev-input"
              placeholder="Short profile of the developer…"
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              disabled={saving}
            />
          </div>
        </div>

        <div className="dev-actions">
          <button type="submit" className="dev-btn" disabled={saving}>
            <Plus className="h-4 w-4" />
            {saving
              ? "Saving…"
              : editing
              ? "Update developer"
              : "Add developer"}
          </button>
          {editing && (
            <button
              type="button"
              className="dev-btn-ghost"
              onClick={onCancel}
              disabled={saving}
            >
              <X className="h-4 w-4" />
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="dev-table-wrap">
        <table className="dev-table">
          <thead>
            <tr>
              <th>Developer</th>
              <th>Description</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={3} className="dev-empty">
                  Loading developers…
                </td>
              </tr>
            )}

            {!loading &&
              items.map((d) => (
                <tr key={d.id}>
                  <td>
                    <div className="dev-name-cell">
                      <span className="dev-avatar">
                        {(d.name || "D")[0].toUpperCase()}
                      </span>
                      {d.name}
                    </div>
                  </td>
                  <td className="dev-desc">{d.description || "—"}</td>
                  <td>
                    <div className="dev-row-actions">
                      <button
                        type="button"
                        className="icon-btn"
                        title="Edit"
                        onClick={() => onEdit(d)}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        className="icon-btn icon-btn--danger"
                        title="Delete"
                        onClick={() => openDeleteModal(d)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={3} className="dev-empty">
                  No developers added yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Delete confirmation modal */}
      {deleteId && (
        <div className="dev-modal-backdrop" role="dialog" aria-modal="true">
          <div className="dev-modal">
            <h3 className="dev-modal-title">Delete developer?</h3>
            <p className="dev-modal-text">
              Are you sure you want to delete <strong>{deleteName}</strong>?
              This action cannot be undone.
            </p>
            <div className="dev-modal-actions">
              <button
                type="button"
                className="dev-btn-ghost"
                onClick={closeDeleteModal}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="dev-btn-danger"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting…" : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}